import {
  BoxIcon,
  BriefcaseIcon,
  BuildingIcon,
  CalendarCheckIcon,
  CarIcon,
  ClipboardListIcon,
  FactoryIcon,
  FileSignatureIcon,
  FolderKanbanIcon,
  GraduationCapIcon,
  HammerIcon,
  HandshakeIcon,
  HeartPulseIcon,
  HouseIcon,
  LandmarkIcon,
  LayersIcon,
  PackageIcon,
  PlaneIcon,
  ScrollTextIcon,
  ShieldCheckIcon,
  StoreIcon,
  TicketIcon,
  TruckIcon,
  WrenchIcon,
  type LucideIcon,
} from 'lucide-react';

import type { DynamicType, DynamicTypeFeatures, EntityType, LinkKind, Stage } from '../api';

/**
 * Icons a user-defined entity type may use («Свои сущности»): the names the API accepts
 * (`DynamicType.Icons` on the server) and their components.
 */
export const TYPE_ICONS: Record<string, LucideIcon> = {
  layers: LayersIcon,
  'file-signature': FileSignatureIcon,
  wrench: WrenchIcon,
  'folder-kanban': FolderKanbanIcon,
  'clipboard-list': ClipboardListIcon,
  briefcase: BriefcaseIcon,
  truck: TruckIcon,
  package: PackageIcon,
  'shield-check': ShieldCheckIcon,
  landmark: LandmarkIcon,
  'graduation-cap': GraduationCapIcon,
  building: BuildingIcon,
  store: StoreIcon,
  hammer: HammerIcon,
  'calendar-check': CalendarCheckIcon,
  ticket: TicketIcon,
  house: HouseIcon,
  factory: FactoryIcon,
  'heart-pulse': HeartPulseIcon,
  car: CarIcon,
  plane: PlaneIcon,
  'scroll-text': ScrollTextIcon,
  handshake: HandshakeIcon,
  box: BoxIcon,
};

export const TYPE_ICON_NAMES = Object.keys(TYPE_ICONS);

export const typeIcon = (name: string | null | undefined): LucideIcon => TYPE_ICONS[name ?? ''] ?? LayersIcon;

/** The side panel type of the cards and forms of items (`?panel=crm-item:<id>`); the other types use their own name. */
export const ITEM_PANEL = 'crm-item';

export const panelTypeOf = (type: EntityType): string => (type === 'item' ? ITEM_PANEL : type);

/** The SPA route of a type's board (`/crm/t/<code>`) and its list (`/crm/t/<code>/list`). */
export const typePath = (code: string, list = false): string =>
  `/crm/t/${encodeURIComponent(code)}${list ? '/list' : ''}`;

/** What a new entity type offers by default (the wizard's start): a client, observers and an amount. */
export const DEFAULT_FEATURES: DynamicTypeFeatures = {
  pipelines: false,
  client: true,
  observers: true,
  amount: true,
  products: false,
  dates: false,
  links: [],
  automation: true,
  processes: true,
  showInMenu: true,
};

/** The code suggested for a title: lowercase latin letters, digits and hyphens (transliterating Cyrillic). */
export function suggestCode(title: string): string {
  const map: Record<string, string> = {
    а: 'a',
    б: 'b',
    в: 'v',
    г: 'h',
    ґ: 'g',
    д: 'd',
    е: 'e',
    є: 'ie',
    ж: 'zh',
    з: 'z',
    и: 'y',
    і: 'i',
    ї: 'i',
    й: 'i',
    к: 'k',
    л: 'l',
    м: 'm',
    н: 'n',
    о: 'o',
    п: 'p',
    р: 'r',
    с: 's',
    т: 't',
    у: 'u',
    ф: 'f',
    х: 'kh',
    ц: 'ts',
    ч: 'ch',
    ш: 'sh',
    щ: 'shch',
    ь: '',
    ю: 'iu',
    я: 'ia',
    ы: 'y',
    э: 'e',
    ъ: '',
    ё: 'e',
  };
  const text = Array.from(title.toLowerCase(), (c) => map[c] ?? c).join('');
  const code = text
    .replace(/[^a-z0-9]+/gu, '-')
    .replace(/^-+|-+$/gu, '')
    .replace(/^[^a-z]+/u, '');
  return code.slice(0, 31);
}

/** The server's rule for codes: 2–31 lowercase latin letters, digits and hyphens, starting with a letter. */
export const isTypeCode = (code: string): boolean => /^[a-z][a-z0-9-]{1,30}$/u.test(code);

/** The stages in progress of a pipeline (the segments of the ribbon). */
export const progressStages = (stages: Stage[]): Stage[] => stages.filter((s) => s.semantic === 'inProgress');

/** A type's default pipeline (else its first). */
export const defaultPipelineOf = (type: DynamicType) =>
  type.pipelines.find((p) => p.isDefault) ?? type.pipelines[0];

/** Whether items of the type may be linked to a kind of records. */
export const allowsLink = (type: DynamicType, kind: LinkKind): boolean => type.features.links.includes(kind);
